<template>
  <v-page>
    <toolbar fixed>
      <template v-slot:left>
        <span class="v-toolbar__text">{{
          $t("pages.account.notifications.notificationTitle")
        }}</span>
      </template>
    </toolbar>

    <div
      v-if="!isPushEnabled() && isSupportedDevice()"
      class="container px-2 mt-4 pb-1 text-center"
    >
      <v-button
        primary
        block
        :is-loading="isLoadingPush"
        @click="requestNotifications"
      >
        <v-icon icon="notification_add" class="mr-1"></v-icon>
        {{ $t("pages.account.notifications.enablePushNotification") }}
      </v-button>
    </div>

    <div v-if="!isFetchedPage" class="d-flex justify-content-center pt-5 mt-5">
      <v-loader />
    </div>
    <template v-else>
      <list
        boxed
        :heading="$t('pages.account.notifications.receiveRemindersAddYourSchedule')"
        class="mt-3"
      >
        <list-item>
          <template slot="left">
            <v-icon icon="event_available" />
          </template>
          <div class="pl-1 v-list-item__title --centered">{{ $t("pages.account.notifications.justForYou") }}</div>
          <template slot="right">
            <v-switch
              :value="receiveReminderJustForYou"
              @input="
                receive_reminders_just_for_you =>
                  updateSettings({ receive_reminders_just_for_you })
              "
            />
          </template>
          <template slot="row">
            <span class="v-list-item__subtitle">
              {{ $t("pages.account.notifications.receiveRemindersAddYourSchedule") }}
            </span>
          </template>
        </list-item>
        <list-item>
          <template slot="left">
            <v-icon icon="event" />
          </template>
          <div class="pl-1 v-list-item__title --centered">
            {{ $t("pages.account.notifications.allScheduleContent") }}
          </div>
          <template slot="right">
            <v-switch
              :value="receiveReminderForApp"
              @input="
                receive_reminders_for_app =>
                  updateSettings({ receive_reminders_for_app })
              "
            />
          </template>
          <template slot="row">
            <span class="v-list-item__subtitle">
              {{ $t("pages.account.notifications.receiveReminderAllSchedule") }}
            </span>
          </template>
        </list-item>
      </list>

      <list
        boxed
        :heading="$t('pages.account.notifications.whenReceiveNotification')"
        class="mt-3"
      >
        <list-item>
          <template slot="left">
            <v-icon icon="circle_notifications" />
          </template>
          <div class="pl-1 v-list-item__title --centered">
            {{ $t("pages.account.notifications.whenStartingNowReminder") }}
          </div>
          <template slot="right">
            <v-switch
              :value="isStartingNowReminders"
              @input="
                is_starting_now_reminder =>
                  updateSettings({ is_starting_now_reminder })
              "
            />
          </template>
          <template slot="row">
            <span class="v-list-item__subtitle">
              {{ $t("pages.account.notifications.whenScheduledActivityStarting") }}
            </span>
          </template>
        </list-item>

        <list-item>
          <template slot="left">
            <v-icon icon="circle_notifications" />
          </template>
          <div class="pl-1 v-list-item__title --centered">
            {{ $t("pages.account.notifications.when15MinuteReminder") }}
          </div>
          <template slot="right">
            <v-switch
              :value="is15MinuteReminders"
              @input="
                is_15_minute_reminder =>
                  updateSettings({ is_15_minute_reminder })
              "
            />
          </template>
          <template slot="row">
            <span class="v-list-item__subtitle">
              {{ $t("pages.account.notifications.when15MinuteBeforeActivityStarting") }}
            </span>
          </template>
        </list-item>

        <list-item>
          <template slot="left">
            <v-icon icon="circle_notifications" />
          </template>
          <div class="pl-1 v-list-item__title --centered">{{ $t("pages.account.notifications.whenDailyReminder") }}</div>
          <template slot="right">
            <v-switch
              :value="isDailyReminders"
              @input="
                is_daily_reminders => updateSettings({ is_daily_reminders })
              "
            />
          </template>
          <template slot="row">
            <div class="d-flex flex-column daily-time w-100">
              <span class="v-list-item__subtitle">
                {{ $t("pages.account.notifications.whenDailyReminderContentHeading") }}
              </span>
              <template v-if="isDailyReminders">
                <div class="mt-2 daily-time__heading">
                  {{ $t("pages.account.notifications.whenReceiveNotificationDaily") }}
                </div>
                <button-group class="daily-time__buttons mt-1">
                  <button-group-button
                    light
                    :is-active="remindDayOf"
                    class="w-50"
                    @click="() => updateSettings({ remind_day_of: true })"
                  >
                  {{ $t("pages.account.notifications.When8AMDaily") }}
                  </button-group-button>
                  <button-group-button
                    light
                    :is-active="!remindDayOf"
                    class="w-50"
                    @click="() => updateSettings({ remind_day_of: false })"
                  >
                  {{ $t("pages.account.notifications.when8PMDaily") }}
                  </button-group-button>
                </button-group>
              </template>
            </div>
          </template>
        </list-item>

        <list-item>
          <template slot="left">
            <v-icon icon="circle_notifications" />
          </template>
          <div class="pl-1 v-list-item__title --centered">{{ $t("pages.account.notifications.whenWeeklyReminder") }}</div>
          <template slot="right">
            <v-switch
              :value="isWeeklyReminders"
              @input="
                is_weekly_reminders => updateSettings({ is_weekly_reminders })
              "
            />
          </template>
          <template slot="row">
            <span class="v-list-item__subtitle">
              {{ $t("pages.account.notifications.whenWeeklyReminderContentHeading") }}
            </span>
          </template>
        </list-item>
      </list>

      <list boxed :heading="$t('pages.account.notifications.appNotifications')" class="mt-3">
        <list-item>
          <template slot="left">
            <v-icon icon="chat" />
          </template>
          <div class="pl-1 v-list-item__title --centered">{{ $t("pages.account.notifications.messages") }}</div>
          <template slot="right">
            <v-switch
              :value="receiveMessageNotifications"
              @input="
                receive_message_notifications =>
                  updateSettings({ receive_message_notifications })
              "
            />
          </template>
          <template slot="row">
            <span class="v-list-item__subtitle">
              {{ $t("pages.account.notifications.notifyDirectOrReplyConversation") }}
            </span>
          </template>
        </list-item>
        <list-item>
          <template slot="left">
            <v-icon icon="circle_notifications" />
          </template>
          <div class="pl-1 v-list-item__title --centered">
            {{ $t("pages.account.notifications.appNotifications") }}
          </div>
          <template slot="right">
            <v-switch
              :value="receiveBlastNotifications"
              @input="
                receive_blast_notifications =>
                  updateSettings({ receive_blast_notifications })
              "
            />
          </template>
          <template slot="row">
            <span class="v-list-item__subtitle">
              {{ $t("pages.account.notifications.notifyNewAppAndUpdate") }}
            </span>
          </template>
        </list-item>
      </list>

      <list
        boxed
        :heading="$t('pages.account.notifications.howReceiveNotification')"
        class="mt-3"
      >
        <list-item>
          <template slot="left">
            <v-icon icon="notifications_active" />
          </template>
          <div class="pl-1 v-list-item__title --centered">
            {{ $t("pages.account.notifications.pushNotification") }}
          </div>
          <template slot="right">
            <v-switch
              :value="receiveReminderViaPushNotification"
              @input="
                receive_reminders_via_push_notification =>
                  updateSettings({ receive_reminders_via_push_notification })
              "
            />
          </template>
        </list-item>
        <list-item>
          <template slot="left">
            <v-icon icon="mark_email_unread" />
          </template>
          <div class="pl-1 v-list-item__title --centered">{{ $t("pages.account.notifications.email") }}</div>
          <template slot="right">
            <v-switch
              :value="receiveReminderViaEmail"
              @input="
                receive_reminders_via_email =>
                  updateSettings({ receive_reminders_via_email })
              "
            />
          </template>
        </list-item>
      </list>

      <v-button
        ghost
        block
        :class="
          `disable-notifications__button mt-4 ${
            isAllSettingsDisabled ? '--on' : '--off'
          }`
        "
        @click="toggleAllSettings"
      >
        {{ $t("pages.account.notifications.turnNotification", {turnState: isAllSettingsDisabled ? $t("pages.account.notifications.on") : $t("pages.account.notifications.off")} ) }}
      </v-button>

      <div class="text-center">
        <v-button
          v-if="isPushEnabled() && isSupportedDevice()"
          class="mt-4 disable-push__button"
          small
          ghost
          :is-loading="isLoadingPush"
          @click="disablePush"
        >
        {{ $t("pages.account.notifications.disablePushNotifications") }}
        </v-button>
      </div>

      <alert-dialog
        :actions="[
          {
            title: $t('pages.account.notifications.dialogTurnAllCancel'),
            callback: () => (this.showConfirmModal = false)
          },
          {
            title: $t('pages.account.notifications.dialogTurnAllYes'),
            callback: confirmToggleAllSettings
          }
        ]"
        :show="showConfirmModal"
        :title="$t('pages.account.notifications.dialogTurnAllConfirmTitle')"
        centered
        @close="() => (this.showConfirmModal = false)"
      >
        <p class="font-semi-bold">
          {{ $t("pages.account.notifications.dialogTurnAllConfirmContent") }}
        </p>
      </alert-dialog>
    </template>
  </v-page>
</template>

<script>
import { mapActions, mapState, mapGetters } from "vuex"
import { set, get, LS_KEYS } from "@/lib/local-storage"

import {
  unsubscribeFromPushNotifications,
  requestPermission,
  isSupportedDevice,
  queryPushStatus
} from "@/lib/push-notification"

import { STAGES } from "@/components/modals/NotificationPermissionTrigger"
import Toolbar from "@/components/Toolbar"
import List from "@/components/List"
import ListItem from "@/components/ListItem"
import vSwitch from "@/components/forms/vSwitch"

export default {
  components: {
    Toolbar,
    List,
    ListItem,
    vSwitch,
    ButtonGroup: () => import("@/components/ButtonGroup"),
    ButtonGroupButton: () => import("@/components/ButtonGroupButton"),
    AlertDialog: () => import("@/components/AlertDialog")
  },

  data() {
    return {
      isLoading: true,
      isFetchedPage: false,
      isLoadingPush: false,
      showConfirmModal: false,
      pushStatusInterval: null
    }
  },

  mounted() {
    this.setupPage()
    this.loadPage()
  },

  beforeDestroy() {
    this.teardownPage()
  },

  activated() {
    this.setupPage()
  },

  deactivated() {
    this.teardownPage()
  },

  computed: {
    ...mapGetters("auth", [
      "currentUser",
      "isAuthenticated",
      "hasCheckedAuthentication"
    ]),
    ...mapState("notifications", [
      "canAsk",
      "receiveBlastNotifications",
      "receiveMessageNotifications",
      "receiveReminderJustForYou",
      "receiveReminderForApp",
      "receiveReminderViaPushNotification",
      "receiveReminderViaEmail",
      "isDailyReminders",
      "isWeeklyReminders",
      "is15MinuteReminders",
      "isStartingNowReminders",
      "remindDayOf"
    ]),
    ...mapGetters("notifications", ["isPushEnabled"]),

    isAllSettingsDisabled() {
      return !Object.values(this.notificationSettings).includes(true)
    },

    notificationSettings() {
      return {
        receive_blast_notifications: this.receiveBlastNotifications,
        receive_message_notifications: this.receiveMessageNotifications,
        receive_reminders_just_for_you: this.receiveReminderJustForYou,
        receive_reminders_for_app: this.receiveReminderForApp,
        receive_reminders_via_push_notification: this
          .receiveReminderViaPushNotification,
        receive_reminders_via_email: this.receiveReminderViaEmail,
        is_daily_reminders: this.isDailyReminders,
        is_weekly_reminders: this.isWeeklyReminders,
        is_15_minute_reminder: this.is15MinuteReminders,
        is_starting_now_reminder: this.isStartingNowReminders
      }
    }
  },

  watch: {
    isAuthenticated: {
      immediate: true,
      handler: function() {
        this.loadPage()
      }
    },

    receiveReminderViaPushNotification(value) {
      if (value && !this.isPushEnabled()) {
        this.requestNotifications()
      }
    }
  },

  methods: {
    isSupportedDevice,

    ...mapActions("notifications", [
      "fetchNotificationSettings",
      "updateNotificationSettings"
    ]),

    setupPage() {
      queryPushStatus()
      this.pushStatusInterval = setInterval(queryPushStatus, 1500)
    },

    teardownPage() {
      if (this.pushStatusInterval) {
        clearInterval(this.pushStatusInterval)
        this.pushStatusInterval = null
      }
    },

    requestNotifications() {
      this.isLoadingPush = true

      requestPermission()
        .then(() => {
          this.$router.push(STAGES.GRANTED)
          this.updateSettings({
            receive_reminders_via_push_notification: true
          })
        })
        .catch(err => {
          console.log(err)
          this.$router.push(STAGES.DENIED)
        })
        .finally(() => {
          this.isLoadingPush = false
        })
    },

    disablePush() {
      this.isLoadingPush = true
      unsubscribeFromPushNotifications().then(() => {
        this.isLoadingPush = false
      })
    },

    loadPage() {
      if (!this.isAuthenticated) return

      this.isLoading = true
      this.fetchNotificationSettings().finally(() => {
        this.isLoading = false
        this.isFetchedPage = true
      })
    },

    updateSettings(data) {
      const willDisableAll = !Object.values({
        ...this.notificationSettings,
        ...data
      }).includes(true)

      if (
        willDisableAll &&
        (Object.values(data).length > 1 ||
          data.receive_reminders_via_push_notification == undefined)
      ) {
        // Store last push enabled state when disabling all notifications and:
        // 1. More than 1 enabled notifications
        // 2. There is 1 enabled notification but not receive_reminders_via_push_notification

        set(LS_KEYS.LAST_PUSH_ENABLED, this.receiveReminderViaPushNotification)
      }

      this.updateNotificationSettings({
        receive_blast_notifications: this.receiveBlastNotifications,
        receive_message_notifications: this.receiveMessageNotifications,
        receive_reminders_just_for_you: this.receiveReminderJustForYou,
        receive_reminders_for_app: this.receiveReminderForApp,
        is_daily_reminders: this.isDailyReminders,
        is_weekly_reminders: this.isWeeklyReminders,
        is_15_minute_reminder: this.is15MinuteReminders,
        is_starting_now_reminder: this.isStartingNowReminders,
        remind_day_of: this.remindDayOf,
        ...data,
        ...{
          receive_reminders_via_push_notification: this.getPushEnabledValue(
            data
          ),
          receive_reminders_via_email: this.getEmailEnabledValue(data)
        }
      })
    },

    getPushEnabledValue(settings = {}) {
      if (this.isAllSettingsDisabled) {
        if (Object.values(settings).length == 1) {
          if (settings.receive_reminders_via_email != undefined) {
            // When all notifications are disabled and enable email -> don't update push
            return this.receiveReminderViaPushNotification
          } else if (
            settings.receive_reminders_via_push_notification != undefined
          ) {
            // When toggle push manually
            return settings.receive_reminders_via_push_notification
          } else {
            // When all notifications are disabled and enable 1
            return get(LS_KEYS.LAST_PUSH_ENABLED, true)
          }
        } else {
          // When all notifications are disabled and enable all
          return get(LS_KEYS.LAST_PUSH_ENABLED, true)
        }
      } else {
        return settings.receive_reminders_via_push_notification === undefined
          ? this.receiveReminderViaPushNotification
          : settings.receive_reminders_via_push_notification
      }
    },

    getEmailEnabledValue(settings = {}) {
      if (this.isAllSettingsDisabled) {
        if (Object.values(settings).length === 1) {
          return settings.receive_reminders_via_push_notification != undefined
            ? this.receiveReminderViaEmail
            : true
        } else {
          return settings.receive_reminders_via_email
        }
      } else {
        return settings.receive_reminders_via_email === undefined
          ? this.receiveReminderViaEmail
          : settings.receive_reminders_via_email
      }
    },

    confirmToggleAllSettings() {
      const value = this.isAllSettingsDisabled

      this.updateSettings({
        receive_blast_notifications: value,
        receive_message_notifications: value,
        receive_reminders_just_for_you: value,
        receive_reminders_for_app: value,
        receive_reminders_via_push_notification: value,
        receive_reminders_via_email: value,
        is_daily_reminders: value,
        is_weekly_reminders: value,
        is_15_minute_reminder: value,
        is_starting_now_reminder: value
      })

      this.showConfirmModal = false
    },

    toggleAllSettings() {
      const value = this.isAllSettingsDisabled

      if (!value) {
        this.showConfirmModal = true
      } else {
        this.confirmToggleAllSettings()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables";

.v-list-item__title {
  font-weight: bold;

  &.--centered {
    padding-top: 3px;
  }
}

.v-list-item__subtitle {
  padding: 0 2.25rem;
  margin-top: -0.5rem;
  font-size: 13px;
  font-weight: 500;
  color: $color-ui--grey-70;
  opacity: 1;
}

.daily-time {
  &__heading,
  &__buttons {
    margin-left: 2.25rem;
  }

  &__heading {
    font-size: 13px;
    font-weight: 500;
  }

  &__buttons {
    background-color: #eaf0f5;
    border-radius: 7px;
    padding: 5px;

    .v-button {
      font-size: 12px;
      border-radius: 7px !important;
    }
  }
}

.disable-notifications__button {
  font-size: 14px;
  color: $color-ui--black;

  &.--on {
    color: $color-ui--green;
  }
}

.disable-push__button {
  color: $color-ui--red;
}

.v-radio__wrapper {
  height: 30px;
  width: 40px;
}

.v-list-item__left {
  display: flex;
  align-items: center;
}
</style>
